import { clsx } from "clsx";
import type { ReactNode } from "react";

const variantStyles = {
  info: {
    container:
      "border-blue-400 bg-blue-50 text-blue-800 dark:border-blue-500/40 dark:bg-blue-950/30 dark:text-blue-300",
    icon: "text-blue-500 dark:text-blue-400",
  },
  success: {
    container:
      "border-green-400 bg-green-50 text-green-800 dark:border-green-500/40 dark:bg-green-950/30 dark:text-green-300",
    icon: "text-green-500 dark:text-green-400",
  },
  warning: {
    container:
      "border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-300",
    icon: "text-amber-500 dark:text-amber-400",
  },
  error: {
    container:
      "border-red-400 bg-red-50 text-red-800 dark:border-red-500/40 dark:bg-red-950/30 dark:text-red-300",
    icon: "text-red-500 dark:text-red-400",
  },
} as const;

export interface AlertProps {
  variant: keyof typeof variantStyles;
  title?: string;
  children: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

export function Alert({
  variant,
  title,
  children,
  onDismiss,
  className,
}: AlertProps) {
  const styles = variantStyles[variant];

  return (
    <div
      role="alert"
      className={clsx(
        "flex gap-3 rounded-lg border-l-4 p-4",
        styles.container,
        className,
      )}
    >
      <div className={clsx("shrink-0 pt-0.5", styles.icon)}>
        <AlertIcon variant={variant} />
      </div>

      <div className="flex-1 text-sm">
        {title && <p className="mb-1 font-medium">{title}</p>}
        <div>{children}</div>
      </div>

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 cursor-pointer rounded-md p-1 opacity-60 transition-opacity hover:opacity-100"
          aria-label="Zamknij"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

function AlertIcon({ variant }: { variant: keyof typeof variantStyles }) {
  const iconPaths: Record<keyof typeof variantStyles, string> = {
    info: "M12 16v-4m0-4h.01M22 12a10 10 0 11-20 0 10 10 0 0120 0z",
    success: "M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    warning:
      "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z",
    error:
      "M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  };

  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={iconPaths[variant]} />
    </svg>
  );
}
